import {Link, SplitPageLayout} from '@primer/react'
import {CommentDiscussionIcon} from '@primer/octicons-react'
import {getDatadogRumViewName, setDatadogRumViewName} from '@github-ui/datadog-rum'
import {OverviewMergeBox} from '../components/overview/mergebox/OverviewMergeBox'
import {Description} from '../components/overview/Description'
import {StickySidebar} from '@github-ui/sticky-sidebar'
import {MINI_HEADER_HEIGHT} from '../helpers/constants'
import {
  DockedPanel,
  useDockedPanelActions,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../contexts/DockedPanelContext'
import {isModernPullRequestExperienceEnabled} from '@github-ui/pull-request-feature-flags'
import {SIDEBAR_WITH_PANEL_BREAKPOINT, useMinWidth} from '../helpers/measure-viewport'
import styles from './Overview.module.css'
import {ForYouSection} from '../components/overview/for-you/ForYouSection'
import {OverviewInfoContent} from '../components/overview/OverviewInfoContent'
import {clsx} from 'clsx'
import {useEffect} from 'react'
import {CommentsSection} from '../components/overview/CommentsSection'
import {OverviewAnalyticsProvider} from '../components/overview/data/OverviewAnalyticsContext'
import {OverviewRouteContext} from '../components/overview/data/use-overview-route'
import {pullRequestsOverviewIndexRoute, pullRequestsOverviewRoute} from './overview-route'
import {getPanelIcon} from '../components/toolbar/panel-registry'
import {usePullRequestState} from '../hooks/use-pull-request-state'
import {PullRequestState} from '../types'

const MergeStatusIcon = getPanelIcon(DockedPanel.MergeStatus)
const TimelineIcon = getPanelIcon(DockedPanel.Timeline)
const OVERVIEW_SIDEBAR_ANCHOR_ID = 'overview-status-and-details'

// Overview mounts under two routes - `/pull/:number/overview` and `/pull/:number` (when it replaces the conversation).
// The route context is used to determine which route is currently mounted, so that data hooks can read the correct route without throwing an error.
export function OverviewRouteEntrypoint() {
  return (
    <OverviewRouteContext value={pullRequestsOverviewRoute}>
      <OverviewEntrypoint />
    </OverviewRouteContext>
  )
}

export function OverviewIndexRouteEntrypoint() {
  // When the overview replaces the conversation as the PR index route, rename the RUM view
  // (rather than starting a new one) so it's distinguishable from the standard layout view in dashboards.
  useEffect(() => {
    void setDatadogRumViewName(`${getDatadogRumViewName()}_overview`)
  }, [])

  return (
    <OverviewRouteContext value={pullRequestsOverviewIndexRoute}>
      <OverviewEntrypoint />
    </OverviewRouteContext>
  )
}

export function OverviewEntrypoint() {
  return (
    <OverviewAnalyticsProvider>
      <OverviewComponent />
    </OverviewAnalyticsProvider>
  )
}

function OverviewComponent() {
  const {openPanel} = useDockedPanelActions()
  const isDockedPanelOpen = useIsDockedPanelOpen()
  const isCompact = useIsDockedPanelCompact()
  const hasRoomForBoth = useMinWidth(SIDEBAR_WITH_PANEL_BREAKPOINT)
  const showOverviewSidebar = !isDockedPanelOpen || isCompact || hasRoomForBoth
  const showUpdatedHeader = isModernPullRequestExperienceEnabled()

  const isMerged = usePullRequestState() === PullRequestState.Merged

  return (
    <div className={clsx('width-full', styles.layout)} data-side-panel-open={isDockedPanelOpen && !isCompact}>
      <SplitPageLayout className={styles.wrapper}>
        <SplitPageLayout.Content as="div" padding="none" className={styles.content}>
          <div className="d-flex gap-4 flex-column">
            <ForYouSection />
            <Description />
            <CommentsSection skipTargetId={showOverviewSidebar ? OVERVIEW_SIDEBAR_ANCHOR_ID : undefined} />
            <OverviewMergeBox />
          </div>
          <footer className="py-4 text-center color-fg-muted">
            <p className="m-0">
              <strong className="color-fg-default">Looking for an old friend?</strong>{' '}
              {isMerged ? (
                <>
                  Try visiting the new home for the{' '}
                  <Link
                    as="button"
                    inline
                    onClick={event => openPanel(DockedPanel.Timeline, {returnFocusTo: event.currentTarget})}
                  >
                    timeline
                  </Link>{' '}
                  <PanelIcons />.
                </>
              ) : (
                <>
                  Try visiting the new homes for{' '}
                  <Link
                    as="button"
                    inline
                    onClick={event => openPanel(DockedPanel.MergeStatus, {returnFocusTo: event.currentTarget})}
                  >
                    merge box
                  </Link>{' '}
                  and the{' '}
                  <Link
                    as="button"
                    inline
                    onClick={event => openPanel(DockedPanel.Timeline, {returnFocusTo: event.currentTarget})}
                  >
                    timeline
                  </Link>{' '}
                  <PanelIcons />.
                </>
              )}
            </p>
          </footer>
        </SplitPageLayout.Content>
        {showOverviewSidebar && (
          <SplitPageLayout.Pane
            aria-labelledby={OVERVIEW_SIDEBAR_ANCHOR_ID}
            position="end"
            divider="none"
            padding="none"
            width="large"
            sticky={false}
            className={styles.panel}
          >
            <h3 className="sr-only" id={OVERVIEW_SIDEBAR_ANCHOR_ID} tabIndex={-1}>
              Status and details
            </h3>

            <StickySidebar topOffset={showUpdatedHeader ? MINI_HEADER_HEIGHT : 0} className={styles.sidebar}>
              <OverviewInfoContent />
            </StickySidebar>
          </SplitPageLayout.Pane>
        )}
      </SplitPageLayout>
    </div>
  )
}

function PanelIcons() {
  return (
    <span className={styles.panelIcons} aria-hidden="true">
      <span className={styles.panelIcon}>
        <MergeStatusIcon size={12} />
      </span>
      <span className={styles.panelIcon}>
        <TimelineIcon size={12} />
      </span>
      <span className={styles.panelIcon}>
        <CommentDiscussionIcon size={12} />
      </span>
    </span>
  )
}
